import {
  clamp,
  collectShiftedValuePairs,
  getFeatureRectangleCells,
  getTargetCellCenterFeet,
  validate2DArrayShape,
} from './algorithmShared'
import {
  MATCH_CONFIDENCE_COVERAGE_WEIGHT,
  MATCH_CONFIDENCE_DISTANCE_WEIGHT,
  MATCH_CONFIDENCE_PATTERN_WEIGHT,
  NEUTRAL_PATTERN_MISMATCH,
  PRIOR_SUPPORT_DISTANCE_WEIGHT,
  PRIOR_SUPPORT_MIN_COVERAGE_FRACTION,
  PRIOR_SUPPORT_MIN_VALUE_COUNT,
  PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
  PRIOR_SUPPORT_SEARCH_RADIUS_FEET,
  PRIOR_SUPPORT_TRANSLATION_STEP_BINS,
  TARGET_FEATURE_RECT_PADDING_FEET,
} from './constants'
import type {
  MatchShortTermFeatureRectanglesProps,
  MatchShortTermFeatureRectanglesResult,
  PriorSupportSearchOptions,
  RectanglePriorSupportCandidate,
  ShortTermCorrosionCellBounds,
  ShortTermCorrosionFeatureRectangle,
  ShortTermCorrosionPriorSupport,
  ShortTermGridValues,
  ShortTermRateFeature,
  ShortTermRateFeatureCell,
  ShortTermRateFeatureGridMetadata,
  ShortTermRatePriorSupport,
  ValuePair,
} from './types'

export function matchShortTermCorrosionFeatureRectangles({
  featureRectangles,
  targetThicknessValues,
  comparisonThicknessValues,
  targetGrid,
  comparisonGrid,
  targetFeatureRectPaddingFeet = TARGET_FEATURE_RECT_PADDING_FEET,
  priorSupportSearchRadiusFeet = PRIOR_SUPPORT_SEARCH_RADIUS_FEET,
  priorSupportTranslationStepBins = PRIOR_SUPPORT_TRANSLATION_STEP_BINS,
  priorSupportDistanceWeight = PRIOR_SUPPORT_DISTANCE_WEIGHT,
  priorSupportMissingCoverageWeight = PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
  priorSupportMinValueCount = PRIOR_SUPPORT_MIN_VALUE_COUNT,
}: MatchShortTermFeatureRectanglesProps): MatchShortTermFeatureRectanglesResult {
  validate2DArrayShape(targetThicknessValues, targetGrid)
  validate2DArrayShape(comparisonThicknessValues, comparisonGrid)

  const priorSupports: ShortTermCorrosionPriorSupport[] = []

  for (const featureRectangle of featureRectangles) {
    const support = findPriorSupportForFeatureRectangle({
      featureRectangle,
      targetThicknessValues,
      comparisonThicknessValues,
      targetGrid,
      comparisonGrid,
      options: {
        rectPaddingFeet: targetFeatureRectPaddingFeet,
        searchRadiusFeet: priorSupportSearchRadiusFeet,
        translationStepBins: priorSupportTranslationStepBins,
        distanceWeight: priorSupportDistanceWeight,
        missingCoverageWeight: priorSupportMissingCoverageWeight,
        minValueCount: priorSupportMinValueCount,
      },
    })
    if (support) priorSupports.push(stripRectangleSupportValues(support))
  }

  return { priorSupports }
}

export function findPriorSupportForTargetFeature({
  targetFeature,
  targetThicknessValues,
  comparisonThicknessValues,
  targetGrid,
  comparisonGrid,
  options = {},
}: {
  targetFeature: ShortTermRateFeature
  targetThicknessValues: ShortTermGridValues
  comparisonThicknessValues: ShortTermGridValues
  targetGrid: ShortTermRateFeatureGridMetadata
  comparisonGrid: ShortTermRateFeatureGridMetadata
  options?: PriorSupportSearchOptions
}): ShortTermRatePriorSupport | undefined {
  const searchRadiusFeet = Math.max(
    0,
    options.searchRadiusFeet ?? PRIOR_SUPPORT_SEARCH_RADIUS_FEET
  )
  const translationStepBins = Math.max(
    1,
    Math.floor(
      options.translationStepBins ?? PRIOR_SUPPORT_TRANSLATION_STEP_BINS
    )
  )
  const stepXFeet = Math.max(
    Math.min(targetGrid.binWidthFeet, comparisonGrid.binWidthFeet) *
      translationStepBins,
    Number.EPSILON
  )
  const stepYFeet = Math.max(
    Math.min(targetGrid.binHeightFeet, comparisonGrid.binHeightFeet) *
      translationStepBins,
    Number.EPSILON
  )
  const maxXStep = Math.ceil(searchRadiusFeet / stepXFeet)
  const maxYStep = Math.ceil(searchRadiusFeet / stepYFeet)
  const rectangleCells = getFeatureRectangleCells({
    targetFeature,
    targetGrid,
    paddingFeet: options.rectPaddingFeet ?? TARGET_FEATURE_RECT_PADDING_FEET,
  })
  let bestCandidate: ShortTermRatePriorSupport | undefined

  for (let yStep = -maxYStep; yStep <= maxYStep; yStep += 1) {
    const shiftYFeet = yStep * stepYFeet
    for (let xStep = -maxXStep; xStep <= maxXStep; xStep += 1) {
      const shiftXFeet = xStep * stepXFeet
      const translationDistanceFeet = Math.hypot(shiftXFeet, shiftYFeet)
      if (translationDistanceFeet > searchRadiusFeet) continue

      const candidate = scorePriorSupportCandidate({
        targetFeature,
        rectangleCells,
        targetThicknessValues,
        comparisonThicknessValues,
        targetGrid,
        comparisonGrid,
        shiftXFeet,
        shiftYFeet,
        searchRadiusFeet,
        translationDistanceFeet,
        distanceWeight: options.distanceWeight ?? PRIOR_SUPPORT_DISTANCE_WEIGHT,
        minCoverageFraction:
          options.minCoverageFraction ?? PRIOR_SUPPORT_MIN_COVERAGE_FRACTION,
        missingCoverageWeight:
          options.missingCoverageWeight ??
          PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
        minValueCount: options.minValueCount ?? PRIOR_SUPPORT_MIN_VALUE_COUNT,
      })
      if (!candidate) continue

      if (!bestCandidate || isBetterPriorSupport(candidate, bestCandidate)) {
        bestCandidate = candidate
      }
    }
  }

  return bestCandidate
}

export function findPriorSupportForFeatureRectangle({
  featureRectangle,
  targetThicknessValues,
  comparisonThicknessValues,
  targetGrid,
  comparisonGrid,
  options = {},
}: {
  featureRectangle: ShortTermCorrosionFeatureRectangle
  targetThicknessValues: ShortTermGridValues
  comparisonThicknessValues: ShortTermGridValues
  targetGrid: ShortTermRateFeatureGridMetadata
  comparisonGrid: ShortTermRateFeatureGridMetadata
  options?: PriorSupportSearchOptions
}): RectanglePriorSupportCandidate | undefined {
  const searchRadiusFeet = Math.max(
    0,
    options.searchRadiusFeet ?? PRIOR_SUPPORT_SEARCH_RADIUS_FEET
  )
  const translationStepBins = Math.max(
    1,
    Math.floor(
      options.translationStepBins ?? PRIOR_SUPPORT_TRANSLATION_STEP_BINS
    )
  )
  const stepXFeet = Math.max(
    Math.min(targetGrid.binWidthFeet, comparisonGrid.binWidthFeet) *
      translationStepBins,
    Number.EPSILON
  )
  const stepYFeet = Math.max(
    Math.min(targetGrid.binHeightFeet, comparisonGrid.binHeightFeet) *
      translationStepBins,
    Number.EPSILON
  )
  const maxXStep = Math.ceil(searchRadiusFeet / stepXFeet)
  const maxYStep = Math.ceil(searchRadiusFeet / stepYFeet)
  const rectangleCells = getFeatureRectangleCells({
    targetCellBounds: featureRectangle.targetCellBounds,
    targetGrid,
    paddingFeet: options.rectPaddingFeet ?? TARGET_FEATURE_RECT_PADDING_FEET,
  })
  let bestCandidate: RectanglePriorSupportCandidate | undefined

  for (let yStep = -maxYStep; yStep <= maxYStep; yStep += 1) {
    const shiftYFeet = yStep * stepYFeet
    for (let xStep = -maxXStep; xStep <= maxXStep; xStep += 1) {
      const shiftXFeet = xStep * stepXFeet
      const translationDistanceFeet = Math.hypot(shiftXFeet, shiftYFeet)
      if (translationDistanceFeet > searchRadiusFeet) continue

      const candidate = scoreFeatureRectanglePriorSupportCandidate({
        featureRectangle,
        rectangleCells,
        targetThicknessValues,
        comparisonThicknessValues,
        targetGrid,
        comparisonGrid,
        shiftXFeet,
        shiftYFeet,
        searchRadiusFeet,
        translationDistanceFeet,
        distanceWeight: options.distanceWeight ?? PRIOR_SUPPORT_DISTANCE_WEIGHT,
        missingCoverageWeight:
          options.missingCoverageWeight ??
          PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
        minValueCount: options.minValueCount ?? PRIOR_SUPPORT_MIN_VALUE_COUNT,
      })
      if (!candidate) continue

      if (!bestCandidate || isBetterPriorSupport(candidate, bestCandidate)) {
        bestCandidate = candidate
      }
    }
  }

  return (
    bestCandidate ??
    getClosestInBoundsRectanglePriorSupport({
      featureRectangle,
      targetGrid,
      comparisonGrid,
      searchRadiusFeet,
      stepXFeet,
      stepYFeet,
      maxXStep,
      maxYStep,
    })
  )
}

export function scorePriorSupportCandidate({
  targetFeature,
  rectangleCells,
  targetThicknessValues,
  comparisonThicknessValues,
  targetGrid,
  comparisonGrid,
  shiftXFeet,
  shiftYFeet,
  searchRadiusFeet,
  translationDistanceFeet,
  distanceWeight = PRIOR_SUPPORT_DISTANCE_WEIGHT,
  minCoverageFraction = PRIOR_SUPPORT_MIN_COVERAGE_FRACTION,
  missingCoverageWeight = PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
  minValueCount = PRIOR_SUPPORT_MIN_VALUE_COUNT,
}: {
  targetFeature: ShortTermRateFeature
  rectangleCells?: readonly ShortTermRateFeatureCell[]
  targetThicknessValues: ShortTermGridValues
  comparisonThicknessValues: ShortTermGridValues
  targetGrid: ShortTermRateFeatureGridMetadata
  comparisonGrid: ShortTermRateFeatureGridMetadata
  shiftXFeet: number
  shiftYFeet: number
  searchRadiusFeet: number
  translationDistanceFeet: number
  distanceWeight?: number
  minCoverageFraction?: number
  missingCoverageWeight?: number
  minValueCount?: number
}): ShortTermRatePriorSupport | undefined {
  const rectangleCellsToScore =
    rectangleCells ??
    getFeatureRectangleCells({
      targetFeature,
      targetGrid,
      paddingFeet: TARGET_FEATURE_RECT_PADDING_FEET,
    })
  const rectanglePairs = collectShiftedValuePairs({
    cells: rectangleCellsToScore,
    targetValues: targetThicknessValues,
    comparisonValues: comparisonThicknessValues,
    targetGrid,
    comparisonGrid,
    shiftXFeet,
    shiftYFeet,
  })

  if (
    rectanglePairs.coverageFraction < minCoverageFraction ||
    rectanglePairs.pairs.length < minValueCount
  ) {
    return undefined
  }

  const featurePairs = collectShiftedValuePairs({
    cells: targetFeature.cells,
    targetValues: targetThicknessValues,
    comparisonValues: comparisonThicknessValues,
    targetGrid,
    comparisonGrid,
    shiftXFeet,
    shiftYFeet,
  })

  if (
    featurePairs.coverageFraction < minCoverageFraction ||
    featurePairs.pairs.length < minValueCount
  ) {
    return undefined
  }

  const patternMismatch = calculatePatternMismatch(rectanglePairs.pairs)
  const normalizedDistance =
    searchRadiusFeet > 0 ? translationDistanceFeet / searchRadiusFeet : 0
  const score =
    patternMismatch +
    distanceWeight * normalizedDistance +
    missingCoverageWeight * (1 - rectanglePairs.coverageFraction)

  return {
    targetFeatureId: targetFeature.id,
    shiftXFeet,
    shiftYFeet,
    searchRadiusFeet,
    score,
    coverageFraction: rectanglePairs.coverageFraction,
    patternMismatch,
    normalizedDistance,
    comparisonThicknessValues: featurePairs.pairs.map(
      (pair) => pair.comparisonValue
    ),
  }
}

export function scoreFeatureRectanglePriorSupportCandidate({
  featureRectangle,
  rectangleCells,
  targetThicknessValues,
  comparisonThicknessValues,
  targetGrid,
  comparisonGrid,
  shiftXFeet,
  shiftYFeet,
  searchRadiusFeet,
  translationDistanceFeet,
  distanceWeight = PRIOR_SUPPORT_DISTANCE_WEIGHT,
  missingCoverageWeight = PRIOR_SUPPORT_MISSING_COVERAGE_WEIGHT,
  minValueCount = PRIOR_SUPPORT_MIN_VALUE_COUNT,
}: {
  featureRectangle: ShortTermCorrosionFeatureRectangle
  rectangleCells?: readonly ShortTermRateFeatureCell[]
  targetThicknessValues: ShortTermGridValues
  comparisonThicknessValues: ShortTermGridValues
  targetGrid: ShortTermRateFeatureGridMetadata
  comparisonGrid: ShortTermRateFeatureGridMetadata
  shiftXFeet: number
  shiftYFeet: number
  searchRadiusFeet: number
  translationDistanceFeet: number
  distanceWeight?: number
  missingCoverageWeight?: number
  minValueCount?: number
}): RectanglePriorSupportCandidate | undefined {
  const rectangleCellsToScore =
    rectangleCells ??
    getFeatureRectangleCells({
      targetCellBounds: featureRectangle.targetCellBounds,
      targetGrid,
      paddingFeet: TARGET_FEATURE_RECT_PADDING_FEET,
    })
  const rectanglePairs = collectShiftedValuePairs({
    cells: rectangleCellsToScore,
    targetValues: targetThicknessValues,
    comparisonValues: comparisonThicknessValues,
    targetGrid,
    comparisonGrid,
    shiftXFeet,
    shiftYFeet,
  })

  if (rectanglePairs.pairs.length < minValueCount) {
    return undefined
  }

  const priorCellBounds = getShiftedRectangleCellBounds({
    targetCellBounds: featureRectangle.targetCellBounds,
    targetGrid,
    comparisonGrid,
    shiftXFeet,
    shiftYFeet,
  })
  if (!priorCellBounds) return undefined

  const patternMismatch = calculatePatternMismatch(rectanglePairs.pairs)
  const normalizedDistance =
    searchRadiusFeet > 0 ? translationDistanceFeet / searchRadiusFeet : 0
  const score =
    patternMismatch +
    distanceWeight * normalizedDistance +
    missingCoverageWeight * (1 - rectanglePairs.coverageFraction)

  return {
    featureId: featureRectangle.id,
    shiftXFeet,
    shiftYFeet,
    priorCellBounds,
    score,
    coverageFraction: rectanglePairs.coverageFraction,
    patternMismatch,
    normalizedDistance,
    confidencePercent: calculateShortTermCorrosionMatchConfidencePercent({
      coverageFraction: rectanglePairs.coverageFraction,
      normalizedDistance,
      patternMismatch,
    }),
    searchRadiusFeet,
    comparisonThicknessValues: rectanglePairs.pairs.map(
      (pair) => pair.comparisonValue
    ),
  }
}

function getClosestInBoundsRectanglePriorSupport({
  featureRectangle,
  targetGrid,
  comparisonGrid,
  searchRadiusFeet,
  stepXFeet,
  stepYFeet,
  maxXStep,
  maxYStep,
}: {
  featureRectangle: ShortTermCorrosionFeatureRectangle
  targetGrid: ShortTermRateFeatureGridMetadata
  comparisonGrid: ShortTermRateFeatureGridMetadata
  searchRadiusFeet: number
  stepXFeet: number
  stepYFeet: number
  maxXStep: number
  maxYStep: number
}): RectanglePriorSupportCandidate | undefined {
  let closestSupport: RectanglePriorSupportCandidate | undefined

  for (let yStep = -maxYStep; yStep <= maxYStep; yStep += 1) {
    const shiftYFeet = yStep * stepYFeet
    for (let xStep = -maxXStep; xStep <= maxXStep; xStep += 1) {
      const shiftXFeet = xStep * stepXFeet
      const translationDistanceFeet = Math.hypot(shiftXFeet, shiftYFeet)
      if (translationDistanceFeet > searchRadiusFeet) continue

      const priorCellBounds = getShiftedRectangleCellBounds({
        targetCellBounds: featureRectangle.targetCellBounds,
        targetGrid,
        comparisonGrid,
        shiftXFeet,
        shiftYFeet,
      })
      if (!priorCellBounds) continue

      const normalizedDistance =
        searchRadiusFeet > 0 ? translationDistanceFeet / searchRadiusFeet : 0
      const support: RectanglePriorSupportCandidate = {
        featureId: featureRectangle.id,
        shiftXFeet,
        shiftYFeet,
        priorCellBounds,
        score: 1,
        coverageFraction: 0,
        patternMismatch: 1,
        normalizedDistance,
        confidencePercent: 0,
        searchRadiusFeet,
        comparisonThicknessValues: [],
      }

      if (!closestSupport || isBetterPriorSupport(support, closestSupport)) {
        closestSupport = support
      }
    }
  }

  return closestSupport
}

export function calculateShortTermCorrosionMatchConfidencePercent({
  coverageFraction,
  normalizedDistance,
  patternMismatch,
}: Pick<
  ShortTermCorrosionPriorSupport,
  'coverageFraction' | 'normalizedDistance' | 'patternMismatch'
>): number {
  const penalty =
    MATCH_CONFIDENCE_PATTERN_WEIGHT * clamp(patternMismatch, 0, 1) +
    MATCH_CONFIDENCE_DISTANCE_WEIGHT * clamp(normalizedDistance, 0, 1) +
    MATCH_CONFIDENCE_COVERAGE_WEIGHT * (1 - clamp(coverageFraction, 0, 1))

  return Math.round(clamp(100 * (1 - penalty), 0, 100))
}

export function getShortTermCorrosionMatchConfidencePercent(
  support: ShortTermCorrosionPriorSupport
): number {
  if (Number.isFinite(support.confidencePercent)) {
    return clamp(Math.round(support.confidencePercent ?? 0), 0, 100)
  }

  return calculateShortTermCorrosionMatchConfidencePercent(support)
}

export function getShiftedRectangleCellBounds({
  targetCellBounds,
  targetGrid,
  comparisonGrid,
  shiftXFeet,
  shiftYFeet,
}: {
  targetCellBounds: ShortTermCorrosionCellBounds
  targetGrid: ShortTermRateFeatureGridMetadata
  comparisonGrid: ShortTermRateFeatureGridMetadata
  shiftXFeet: number
  shiftYFeet: number
}): ShortTermCorrosionCellBounds | undefined {
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (let y = targetCellBounds.minY; y <= targetCellBounds.maxY; y += 1) {
    for (let x = targetCellBounds.minX; x <= targetCellBounds.maxX; x += 1) {
      const targetCenter = getTargetCellCenterFeet({ x, y }, targetGrid)
      const comparisonX = Math.floor(
        (targetCenter.xFeet + shiftXFeet - comparisonGrid.xStartFeet) /
          comparisonGrid.binWidthFeet
      )
      const comparisonY = Math.floor(
        (targetCenter.yFeet + shiftYFeet - comparisonGrid.yStartFeet) /
          comparisonGrid.binHeightFeet
      )

      if (
        comparisonX < 0 ||
        comparisonX >= comparisonGrid.xBinCount ||
        comparisonY < 0 ||
        comparisonY >= comparisonGrid.yBinCount
      ) {
        continue
      }

      minX = Math.min(minX, comparisonX)
      maxX = Math.max(maxX, comparisonX)
      minY = Math.min(minY, comparisonY)
      maxY = Math.max(maxY, comparisonY)
    }
  }

  if (
    !Number.isFinite(minX) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxY)
  ) {
    return undefined
  }

  return { minX, maxX, minY, maxY }
}

function calculatePatternMismatch(pairs: readonly ValuePair[]): number {
  if (pairs.length < 2) return NEUTRAL_PATTERN_MISMATCH

  const targetMean =
    pairs.reduce((sum, pair) => sum + pair.targetValue, 0) / pairs.length
  const comparisonMean =
    pairs.reduce((sum, pair) => sum + pair.comparisonValue, 0) / pairs.length
  let covariance = 0
  let targetVariance = 0
  let comparisonVariance = 0

  for (const pair of pairs) {
    const targetResidual = pair.targetValue - targetMean
    const comparisonResidual = pair.comparisonValue - comparisonMean
    covariance += targetResidual * comparisonResidual
    targetVariance += targetResidual ** 2
    comparisonVariance += comparisonResidual ** 2
  }

  if (targetVariance <= 0 || comparisonVariance <= 0) {
    return NEUTRAL_PATTERN_MISMATCH
  }

  const correlation = clamp(
    covariance / Math.sqrt(targetVariance * comparisonVariance),
    -1,
    1
  )

  return (1 - correlation) / 2
}

type PriorSupportComparable = {
  score: number
  normalizedDistance: number
  shiftYFeet: number
  shiftXFeet: number
}

function isBetterPriorSupport<T extends PriorSupportComparable>(
  candidate: T,
  currentBest: T
): boolean {
  if (candidate.score !== currentBest.score) {
    return candidate.score < currentBest.score
  }
  if (candidate.normalizedDistance !== currentBest.normalizedDistance) {
    return candidate.normalizedDistance < currentBest.normalizedDistance
  }
  if (candidate.shiftYFeet !== currentBest.shiftYFeet) {
    return candidate.shiftYFeet < currentBest.shiftYFeet
  }
  return candidate.shiftXFeet < currentBest.shiftXFeet
}

function stripRectangleSupportValues(
  support: RectanglePriorSupportCandidate
): ShortTermCorrosionPriorSupport {
  return {
    featureId: support.featureId,
    shiftXFeet: support.shiftXFeet,
    shiftYFeet: support.shiftYFeet,
    priorCellBounds: support.priorCellBounds,
    score: support.score,
    coverageFraction: support.coverageFraction,
    patternMismatch: support.patternMismatch,
    normalizedDistance: support.normalizedDistance,
    confidencePercent: getShortTermCorrosionMatchConfidencePercent(support),
  }
}
